/** Pure math of print rasters (no WebGL): density alpha rescaling and tiling. */

/**
 * Per-point alpha for a density print. Brightness is `1 − (1 − α)^count`;
 * the print's count per pixel is the screen's times the pixel-area ratio
 * (fixed-size points) times the point-density ratio (the print loads the
 * area fully), so the log-alpha is scaled by the inverse of both:
 * `log(1 − α_p) = log(1 − α_ref) · (mpp_ref / mpp_p)² · (D_ref / D_p)`.
 */
export function printDensityAlpha(options: { alpha: number; referenceMetersPerPixel: number; printMetersPerPixel: number; pointAreaScales: boolean; referencePointsPerM2: number | null; printPointsPerM2: number | null }): number {
  const alpha = Math.min(Math.max(options.alpha, 1e-7), 0.999999);
  const pixelRatio = options.pointAreaScales ? (options.referenceMetersPerPixel / options.printMetersPerPixel) ** 2 : 1;
  const densityRatio = options.referencePointsPerM2 && options.printPointsPerM2 ? options.referencePointsPerM2 / options.printPointsPerM2 : 1;
  const logOneMinus = Math.log(1 - alpha) * pixelRatio * densityRatio;
  return Math.min(Math.max(1 - Math.exp(logOneMinus), 1e-7), 0.999999);
}

/** Tiles covering a w × h raster, each at most `tile` px per side; row-major, no gaps or overlaps. */
export function rasterTiles(width: number, height: number, tile: number): { x: number; y: number; w: number; h: number }[] {
  const tiles: { x: number; y: number; w: number; h: number }[] = [];
  for (let y = 0; y < height; y += tile) {
    for (let x = 0; x < width; x += tile) tiles.push({ x, y, w: Math.min(tile, width - x), h: Math.min(tile, height - y) });
  }
  return tiles;
}
